import React, { useState, useContext } from 'react';
import styles from './AdditionalFieldsForm.module.scss';
import { useTranslation } from 'react-i18next';
import OptionSlider from 'components/common/Form/OptionSlider/OptionSlider';
import MiscellaneousInfo from 'components/ProfileEdit/InterviewEditTab/InterviewTab/Info/MiscellaneousInfo';
import ConsumerInfo from 'components/ProfileEdit/InterviewEditTab/InterviewTab/Info/ConsumerInfo';
import useProfileEditForm from 'components/ProfileEdit/useProfileEditForm';
import { UserContext } from 'context/UserContext';
import Button, { ButtonStyles } from 'components/common/Form/Button';
import { BackIcon } from 'components/common/icons';
import ExperienceInfo from 'components/ProfileEdit/InterviewEditTab/InterviewTab/Info/ExperienceInfo';
import { isCompanyAdmin, isRespondent } from 'constants/UserRoles';

const STEPS_FIELDS = {
    miscellaneous: ['avatar', 'relationshipStatus', 'about'],
    experience: ['education', 'employmentStatus', 'workExperience'],
    consumer: [
        'smartphoneBrand',
        'smartphoneModel',
        'smartphoneColor',
        'tvBrand',
        'tvModel',
        'cars',
        'carModel',
        'brands',
        'newspaper',
        'newsChannels',
        'tvShows',
    ],
};

const AdditionalFieldsForm = () => {
    const { user, setShowAdditionalModal, saveProfile } = useContext(UserContext);
    const { t } = useTranslation();

    const STEPS = {
        miscellaneous: 'profile:text.miscellaneous',
        experience: 'profile:text.experience',
        consumer: 'profile:text.consumer',
    };

    if (!isRespondent(user.role)) {
        delete STEPS.consumer;
        if (isCompanyAdmin(user.role)) delete STEPS.experience;
    }

    const STEPS_ARRAY = Object.values(STEPS);

    const getFirstStepIndex = () => {
        let firstUnfilledStep = 0;
        for (const step in STEPS_FIELDS) {
            let notFilled = false;
            for (const field of STEPS_FIELDS[step]) {
                if (!user[field]) notFilled = true;
            }
            if (notFilled) break;
            firstUnfilledStep++;
        }
        return firstUnfilledStep;
    };

    const [currentStep, setCurrentStep] = useState(STEPS_ARRAY[getFirstStepIndex()]);
    const { forms } = useProfileEditForm(user);
    const isLastStep = currentStep === STEPS_ARRAY[STEPS_ARRAY.length - 1];

    const getForm = () => {
        switch (currentStep) {
            case STEPS.miscellaneous:
                return (
                    <MiscellaneousInfo
                        values={forms.miscellaneous.form.values}
                        onChangeField={forms.miscellaneous.form.onChangeField}
                        isModal
                    />
                );
            case STEPS.experience:
                return (
                    <ExperienceInfo
                        values={forms.experience.form.values}
                        onChangeField={forms.experience.form.onChangeField}
                        isModal
                    />
                );
            case STEPS.consumer:
                return (
                    <ConsumerInfo
                        values={forms.consumer.form.values}
                        onChangeField={forms.consumer.form.onChangeField}
                        isModal
                    />
                );
            default:
                return null;
        }
    };

    const saveData = async () => {
        const saveFormKey = Object.keys(STEPS).find((key) => STEPS[key] === currentStep);
        const saveFormValues = forms[saveFormKey].form.values;
        const data = {};
        for (const key in saveFormValues) {
            // eslint-disable-next-line no-prototype-builtins
            if (saveFormValues.hasOwnProperty(key) && saveFormValues[key]) data[key] = saveFormValues[key];
        }
        await saveProfile(data);
    };

    const handleNext = async () => {
        await saveData();
        const currentStepIndex = STEPS_ARRAY.indexOf(currentStep);
        if (currentStepIndex !== STEPS_ARRAY.length - 1) {
            return setCurrentStep(STEPS_ARRAY[currentStepIndex + 1]);
        }
        return setShowAdditionalModal(false);
    };

    const handleSetStep = async (step) => {
        await saveData();
        setCurrentStep(step);
    };

    const handleSkip = async () => {
        await saveData();
        setShowAdditionalModal(false);
    };

    const buttonText = isLastStep ? t('common.finish') : t('common.continue');

    return (
        <div className={styles.additionalFieldsForm}>
            <div className={styles.header}>{t('profile:text.interview')}:</div>
            <div className={styles.stepper}>
                <OptionSlider
                    options={STEPS_ARRAY}
                    onChange={handleSetStep}
                    value={currentStep}
                    vertical
                    defaultColor={'#f3f3f4'}
                />
            </div>
            <div className={styles.form}>
                {getForm()}
                <div className={styles.buttonWrapper}>
                    <Button text={buttonText} btnStyle={ButtonStyles.primary} onClick={handleNext} />
                </div>
            </div>
            <div className={styles.bottomSkip} onClick={handleSkip}>
                {t('welcome:text.skip')}
                <BackIcon className={styles.skip} />
            </div>
        </div>
    );
};

export default AdditionalFieldsForm;
